import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Briefcase,
  Edit,
  FileText,
  Github,
  Globe,
  Globe2,
  GraduationCap,
  Linkedin,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  Trash2
} from "lucide-react"
import { useState } from "react"
import { useTalent } from "./TalentContext"
import { TalentProfileEditForm } from "./TalentProfileEditForm"
import { CandidateStatusMap, TalentType } from "./types"

type TalentProfileViewProps = {
  talent: TalentType | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TalentProfileView({ talent, open, onOpenChange }: TalentProfileViewProps) {
  const { deleteTalent } = useTalent();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!talent) return null

  // Generate initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  // Toggle edit mode
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Cancel edit and return to view mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  // Handle successful edit submission
  const handleEditSuccess = () => {
    setIsEditMode(false);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!talent) return;

    setIsDeleting(true);
    try {
      await deleteTalent(talent._id);
      onOpenChange(false); // Close the profile view after successful deletion
    } catch (error) {
      console.error("Failed to delete talent:", error);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <div>
              <DialogTitle>{isEditMode ? "Edit Talent Profile" : "Talent Profile"}</DialogTitle>
              {!isEditMode && (
                <DialogDescription>
                  View detailed information about the talent
                </DialogDescription>
              )}
            </div>
          </DialogHeader>

          {isEditMode ? (
            // Edit Mode Content
            <TalentProfileEditForm
              talent={talent}
              onCancel={handleCancelEdit}
              onSuccess={handleEditSuccess}
            />
          ) : (
            // View Mode Content
            <div className="py-4">
              {/* Header Section */}
              <div className="flex gap-6 items-start">
                <Avatar className="h-24 w-24 text-4xl bg-primary text-white font-bold flex items-center justify-center">
                  {getInitials(talent.name)}
                </Avatar>

                <div className="space-y-2 flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-bold">{talent.name}</h2>
                      <p className="text-gray-500">{talent.level} Developer</p>
                    </div>
                    <Badge className={
                      talent.status === "Available"
                        ? "bg-green-100 text-green-800"
                        : talent.status === "In process"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                    }>
                      {CandidateStatusMap[talent.status]}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-y-2 text-sm text-gray-600">
                    {talent.email && (
                      <div className="flex items-center mr-6">
                        <Mail className="h-4 w-4 mr-1" />
                        <span>{talent.email}</span>
                      </div>
                    )}
                    {talent.phone && (
                      <div className="flex items-center mr-6">
                        <Phone className="h-4 w-4 mr-1" />
                        <span>{talent.phone}</span>
                      </div>
                    )}
                    {talent.location && (
                      <div className="flex items-center mr-6">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span>{talent.location}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-2 justify-end">
                    {talent.resumeUrl && (
                      <Button variant="outline" size="sm" className="gap-1">
                        <FileText className="h-4 w-4" />
                        <span>Resume</span>
                      </Button>
                    )}
                    {talent.portfolioUrl && (
                      <Button variant="outline" size="sm" className="gap-1">
                        <Globe className="h-4 w-4" />
                        <span>Portfolio</span>
                      </Button>
                    )}
                    {talent.linkedInUrl && (
                      <Button variant="outline" size="sm" className="gap-1">
                        <Linkedin className="h-4 w-4" />
                        <span>LinkedIn</span>
                      </Button>
                    )}
                    {talent.githubUrl && (
                      <Button variant="outline" size="sm" className="gap-1">
                        <Github className="h-4 w-4" />
                        <span>GitHub</span>
                      </Button>
                    )}

                    {/* Actions dropdown */}
                    {!isEditMode && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={handleDeleteClick}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    <Button
                      className="gap-1"
                      onClick={handleEditClick}
                    >
                      <Edit className="h-4 w-4" />
                      <span>Edit Profile</span>
                    </Button>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              {/* Tabs Section */}
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="experience">Experience</TabsTrigger>
                  <TabsTrigger value="education">Education</TabsTrigger>
                  <TabsTrigger value="notes">Notes</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  {/* Skills */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Skills</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1">
                        {talent.skills.map(skill => (
                          <Badge key={skill} variant="secondary" className="text-sm">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Languages */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Languages</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-4">
                        {talent.languages.map(language => (
                          <div key={language.language} className="flex items-center">
                            <Globe2 className="h-4 w-4 mr-2 text-gray-500" />
                            <span>{language.language}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Additional Information */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Additional Information</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Availability</p>
                        <p>{talent.availability || "Not specified"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Salary Expectation</p>
                        <p>{talent.salary || "Not specified"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p>{talent.lastUpdated}</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="experience" className="space-y-4">
                  <h3 className="text-lg font-medium">Work Experience</h3>
                  <div className="space-y-4">
                    {talent.experience && talent.experience.map((exp, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-start">
                            <Briefcase className="h-5 w-5 mr-3 text-gray-500 mt-0.5" />
                            <div>
                              <p className="font-medium">{exp}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="education" className="space-y-4">
                  <h3 className="text-lg font-medium">Education</h3>
                  <div className="space-y-4">
                    {talent.education.map((edu, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-start">
                            <GraduationCap className="h-5 w-5 mr-3 text-gray-500 mt-0.5" />
                            <div>
                              <p className="font-medium">{edu}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="space-y-4">
                  <h3 className="text-lg font-medium">Notes</h3>
                  <Card>
                    <CardContent className="pt-6">
                      {talent.notes ? (
                        <p className="whitespace-pre-line">{talent.notes}</p>
                      ) : (
                        <p className="text-gray-500 italic">No notes available</p>
                      )}
                    </CardContent>
                    <CardFooter className="flex justify-end pt-2">
                      <Button variant="outline" size="sm" className="gap-1">
                        <Edit className="h-4 w-4" />
                        <span>Add Notes</span>
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the talent profile
              and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={(e) => {
                e.preventDefault(); // Prevent default to handle with custom logic
                handleDeleteConfirm();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
} 